import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import {
  type Api,
  inviteAthlete,
  type InviteAthleteError,
  listProfileLeads,
} from "api-client";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "ui";
import { getRelativeTime } from "utils";

import { DataTable } from "src/components/DataTable";
import { type DataTableFeatures } from "src/components/dataTableFeatures";
import { ProfileLeadSubmissionDialog } from "src/components/ProfileLeadSubmissionDialog";
import { useTableSearch } from "src/components/TableSearchContext";
import { TruncatedTooltip } from "src/components/TruncatedTooltip";

export const PROFILE_LEADS_PAGE_SIZE = 10;

type ProfileLeads = Api.ListProfileLeads.Output.Self;
type ProfileLead = Api.ListProfileLeads.Output.CurrentPage;

interface CoachLeadListProps {
  leads: ProfileLeads;
  selectedLeadId: string | null;
  onSelectedLeadChange: (leadId: string) => void;
  onSelectedLeadClose: () => void;
}

export function CoachLeadList(props: CoachLeadListProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const tableSearch = useTableSearch();
  const toast = useToast();

  const selectedLeadFromPage = props.leads.currentPage.find((lead) => {
    return lead.id === props.selectedLeadId;
  });
  const selectedLeadQuery = useQuery({
    queryKey: ["profile-lead", props.selectedLeadId],
    queryFn: async () => {
      const response = await listProfileLeads({
        query: {
          leadId: props.selectedLeadId!,
          pageIndex: 0,
          pageSize: 1,
        },
      }).then((result) => result.data.data);
      const lead = response.currentPage[0];

      if (!lead) {
        throw new Error("Profile lead not found");
      }

      return lead;
    },
    enabled: Boolean(props.selectedLeadId && !selectedLeadFromPage),
  });
  const selectedLead = selectedLeadFromPage ?? selectedLeadQuery.data;

  const statusLabels: Record<ProfileLead["status"], string> = {
    PENDING: t("athletesPage.leads.statuses.pending"),
    INVITED: t("athletesPage.leads.statuses.invited"),
    REJECTED: t("athletesPage.leads.statuses.rejected"),
    ALREADY_ADDED: t("athletesPage.leads.statuses.alreadyAdded"),
    INVALID_COACH_ACCOUNT: t("athletesPage.leads.statuses.invalid"),
    REQUESTED_BY_ATHLETE: t("athletesPage.leads.statuses.pending"),
  };

  const inviteLeadMutation = useMutation({
    mutationFn: (lead: ProfileLead) => {
      return inviteAthlete({
        body: {
          email: lead.email,
          name: lead.name,
          profileLeadId: lead.pendingLeadId!,
        },
      });
    },
    onSuccess: () => {
      toast.toast({
        title: t("athletesPage.leads.inviteSuccess"),
        variant: "success",
      });
      router.invalidate();
    },
    onError: (error) => {
      toast.toast({
        title:
          (error as unknown as InviteAthleteError).error?.message ??
          t("athletesPage.leads.inviteError"),
        variant: "destructive",
      });
    },
  });

  const columns: ColumnDef<DataTableFeatures, ProfileLead>[] = [
    {
      id: "name",
      header: t("athletesPage.leads.name"),
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      id: "email",
      header: t("athletesPage.leads.email"),
      cell: ({ row }) => (
        <TruncatedTooltip
          text={row.original.email}
          className="block w-full max-w-64"
        />
      ),
    },
    {
      id: "submissions",
      header: t("athletesPage.leads.submissions"),
      cell: ({ row }) => <span>{row.original.submissionCount}</span>,
    },
    {
      id: "status",
      header: t("athletesPage.leads.status"),
      cell: ({ row }) => {
        const lead = row.original;

        const badge = (
          <Badge
            variant={
              lead.status === "PENDING" ||
              lead.status === "REQUESTED_BY_ATHLETE"
                ? "outline"
                : lead.status === "REJECTED" ||
                    lead.status === "INVALID_COACH_ACCOUNT"
                  ? "destructive"
                  : "secondary"
            }
          >
            {statusLabels[lead.status]}
          </Badge>
        );

        if (lead.status === "INVALID_COACH_ACCOUNT") {
          return (
            <Tooltip>
              <TooltipTrigger asChild>{badge}</TooltipTrigger>
              <TooltipContent>
                {t(
                  "athletesPage.leads.statuses.invalidCoachAccountExplanation",
                )}
              </TooltipContent>
            </Tooltip>
          );
        }

        return badge;
      },
    },
    {
      id: "submittedAt",
      header: t("athletesPage.leads.submittedAt"),
      cell: ({ row }) => (
        <span>
          {t(
            "relativeTime",
            getRelativeTime(new Date(row.original.lastSubmittedAt)),
          )}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const lead = row.original;

        return (
          <div className="h-8">
            {lead.pendingLeadId ? (
              <Button
                size="sm"
                loading={
                  inviteLeadMutation.isPending &&
                  inviteLeadMutation.variables.email === lead.email
                }
                disabled={inviteLeadMutation.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  inviteLeadMutation.mutate(lead);
                }}
              >
                {t("athletesPage.leads.invite")}
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={props.leads.currentPage}
        getRowId={(row) => row.email}
        onRowClick={(row) => {
          props.onSelectedLeadChange(row.original.id);
        }}
        searchPlaceholder={t("athletesPage.leads.searchPlaceholder")}
        globalFilter={tableSearch.q}
        onGlobalFilterChange={tableSearch.setSearch}
        manualFiltering
        manualPagination
        rowCount={props.leads.pageInfo.count}
        pagination={tableSearch.pagination}
        onPaginationChange={tableSearch.onPaginationChange}
      />
      {selectedLead ? (
        <ProfileLeadSubmissionDialog
          lead={selectedLead}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              props.onSelectedLeadClose();
            }
          }}
        />
      ) : null}
    </>
  );
}
